import type { BufferGeometry } from 'three';
import type { PaletteModel } from '../../data/types';
import type { LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import { hexToLinear } from '../../core/color';
import { createMeshBuilder, type MeshBuilder } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { beamSolid } from '../fabric/solids';
import { box, rounded } from './lifeGeometry';
import { buildHorseModel, type LifeModel } from './models';

const OAK = hexToLinear('#77583a');
const LEATHER = hexToLinear('#443222');
const IRON = hexToLinear('#73746d');
const FRAME = { origin: [0, 0, 0] as Vec3, yaw: 0 };

function beam(b: MeshBuilder, color: LinearRGB, a: Vec3, z: Vec3, width: number): void {
  beamSolid(
    b,
    { color, surface: SURFACE.wood, edgeId: 0, extent: [0.1, 1], strokeAngle: 0 },
    FRAME,
    a,
    z,
    width
  );
}

/** Broad cattle anatomy and horns distinguish the draught ox from the existing slender horse. */
export function buildOxModel(palette: PaletteModel): LifeModel {
  const horse = buildHorseModel(palette);
  horse.body.dispose();
  const b = createMeshBuilder();
  const coat = hexToLinear('#a18b63');
  rounded(b, coat, [
    { y: 0.96, x: -0.05, depth: 0.9, half: 0.29 },
    { y: 1.22, x: -0.05, depth: 1.05, half: 0.4 },
    { y: 1.57, x: -0.08, depth: 0.86, half: 0.32 },
  ]);
  rounded(b, coat, [
    { y: 0.94, x: 0.65, depth: 0.3, half: 0.28 },
    { y: 1.32, x: 0.7, depth: 0.38, half: 0.32 },
    { y: 1.63, x: 0.83, depth: 0.29, half: 0.25 },
  ]);
  rounded(b, coat, [
    { y: 1.21, x: 1.15, depth: 0.24, half: 0.17 },
    { y: 1.45, x: 1.11, depth: 0.32, half: 0.23 },
    { y: 1.71, x: 0.98, depth: 0.26, half: 0.2 },
  ]);
  rounded(b, hexToLinear('#62533f'), [
    { y: 1.23, x: 1.34, depth: 0.14, half: 0.17 },
    { y: 1.39, x: 1.34, depth: 0.15, half: 0.18 },
  ]);
  for (const side of [-1, 1]) {
    rounded(
      b,
      hexToLinear('#dbd2b2'),
      [
        { y: 1.6, x: 0.99, z: side * 0.17, depth: 0.07, half: 0.07 },
        { y: 1.69, x: 0.96, z: side * 0.37, depth: 0.055, half: 0.055 },
        { y: 1.86, x: 0.99, z: side * 0.48, depth: 0.014, half: 0.014 },
      ],
      6
    );
    beam(b, coat, [0.96, 1.58, side * 0.19], [1.05, 1.53, side * 0.4], 0.045);
    box(b, LEATHER, [1.15, 1.48, side * 0.16 - 0.018], [1.19, 1.51, side * 0.16 + 0.018]);
  }
  beam(b, coat, [-0.99, 1.35, 0], [-1.2, 0.7, 0.03], 0.025);
  beam(b, LEATHER, [-1.2, 0.73, 0.03], [-1.25, 0.56, 0.04], 0.05);
  return {
    ...horse,
    body: b.finish(),
    arm: horse.arm.scale(1.3, 1, 1.4),
    leg: horse.leg.scale(1.3, 1, 1.4),
    shoulderHalf: 0.29,
    hipHalf: 0.3,
    strideM: 0.95,
    stepLiftM: 0.035,
    sole: [horse.sole[0] * 1.3, horse.sole[1] * 1.3, horse.sole[2]],
  };
}

export function buildDraftCart(): BufferGeometry {
  const b = createMeshBuilder();
  for (let row = 0; row < 7; row++)
    box(b, OAK, [-1.18, 0.68, -0.77 + row * 0.22], [1.18, 0.79, -0.56 + row * 0.22]);
  for (const z of [-0.83, 0.83]) {
    for (const x of [-1.11, 0, 1.11])
      box(b, OAK, [x - 0.045, 0.7, z - 0.04], [x + 0.045, 1.25, z + 0.04]);
    for (const y of [0.91, 1.12]) box(b, OAK, [-1.2, y, z - 0.045], [1.2, y + 0.09, z + 0.045]);
  }
  box(b, OAK, [-0.09, 0.42, -1.0], [0.09, 0.55, 1.0]);
  for (const x of [-1.17, 1.17])
    for (const y of [0.91, 1.12]) box(b, OAK, [x - 0.045, y, -0.78], [x + 0.045, y + 0.09, 0.78]);
  return b.finish();
}

export function buildHarness(oxen: boolean): BufferGeometry {
  const b = createMeshBuilder();
  if (oxen) {
    beam(b, OAK, [0.65, 1.42, -1.0], [0.65, 1.42, 1.0], 0.06);
    for (const side of [-1, 1])
      for (const z of [-0.22, 0.22])
        beam(b, LEATHER, [0.65, 1.4, side * 0.56 + z], [0.74, 1.06, side * 0.56 + z], 0.026);
  } else {
    for (const side of [-1, 1])
      beam(b, LEATHER, [0.48, 1.59, side * 0.28], [0.88, 1.14, side * 0.3], 0.045);
    beam(b, LEATHER, [0.88, 1.14, -0.3], [0.88, 1.14, 0.3], 0.045);
  }
  return b.finish();
}

/** Seated clothing follows early-sixteenth-century rider references; the escort is an estimate. */
export function buildMountedRider(escort: boolean): BufferGeometry {
  const b = createMeshBuilder();
  const wool = hexToLinear(escort ? '#655945' : '#4f674c');
  const skin = hexToLinear('#bb966a');
  box(b, LEATHER, [-0.42, 1.49, -0.34], [0.25, 1.57, 0.34]);
  rounded(b, wool, [
    { y: 1.55, x: -0.06, depth: 0.2, half: 0.23 },
    { y: 1.8, x: -0.08, depth: 0.17, half: 0.2 },
    { y: 2.07, x: -0.04, depth: 0.19, half: 0.26 },
    { y: 2.14, x: 0, depth: 0.12, half: 0.14 },
  ]);
  rounded(b, skin, [
    { y: 2.13, x: 0.02, depth: 0.08, half: 0.07 },
    { y: 2.22, x: 0.05, depth: 0.13, half: 0.1 },
    { y: 2.41, x: 0.02, depth: 0.12, half: 0.11 },
  ]);
  rounded(b, escort ? IRON : wool, [
    { y: 2.37, x: 0.01, depth: 0.17, half: 0.15 },
    { y: 2.46, x: -0.01, depth: 0.14, half: 0.12 },
    { y: 2.51, x: -0.02, depth: 0.06, half: 0.06 },
  ]);
  for (const side of [-1, 1]) {
    beam(b, wool, [-0.07, 1.59, side * 0.17], [0.32, 1.24, side * 0.37], 0.095);
    beam(b, LEATHER, [0.32, 1.24, side * 0.37], [0.13, 0.95, side * 0.4], 0.07);
    box(b, LEATHER, [0.08, 0.91, side * 0.4 - 0.065], [0.37, 1.0, side * 0.4 + 0.065]);
    beam(b, wool, [0, 2.01, side * 0.23], [0.23, 1.8, side * 0.3], 0.065);
    const hand: Vec3 = escort && side === 1 ? [0.42, 1.88, 0.48] : [0.58, 1.87, side * 0.17];
    beam(b, wool, [0.23, 1.8, side * 0.3], hand, 0.055);
    if (!escort || side === -1) beam(b, LEATHER, hand, [1.4, 1.83, side * 0.1], 0.009);
  }
  if (escort) {
    box(b, IRON, [0.13, 1.8, -0.19], [0.19, 2.08, 0.19]);
    beam(b, OAK, [0.42, 1.46, 0.48], [0.42, 3.85, 0.48], 0.028);
    rounded(
      b,
      IRON,
      [
        { y: 3.85, x: 0.42, z: 0.48, depth: 0.045, half: 0.045 },
        { y: 4.06, x: 0.42, z: 0.48, depth: 0.002, half: 0.002 },
      ],
      4
    );
  }
  return b.finish();
}
